import { ApiError, NetworkError } from '../lib/api.js';

/**
 * Renders the loading / error state for a TanStack Query result.
 *
 * Three distinct error states matter:
 *
 *  - Auth (Cloudflare Access redirect at the edge, or a 401 from the
 *    Worker if the JWT fails verification): "session expired" + Sign
 *    in button. The button reloads the page so the navigation hits
 *    Access and gets a fresh cookie. Auto-reloading would risk a loop
 *    on a misconfigured AUD, so the user drives the recovery.
 *
 *  - Network (fetch threw — offline, DNS, the kind of thing that
 *    needs the connection to come back, not the user to do anything):
 *    show "offline" copy. TanStack Query is already retrying behind
 *    the scenes.
 *
 *  - Anything else (HTTP 5xx, malformed response): the generic
 *    "Could not load" message stays.
 */
export function LoadStatus({
  isLoading,
  isError,
  error,
}: {
  isLoading: boolean;
  isError: boolean;
  error: unknown;
}) {
  if (isLoading) return <p className="muted">Loading…</p>;
  if (!isError) return null;
  if (error instanceof ApiError && error.isAuth) {
    return (
      <p className="muted">
        Your session has expired.{' '}
        <button
          type="button"
          className="btn btn--small btn--secondary"
          onClick={() => {
            // Re-auth = a real document navigation to the protected
            // origin: with the Access cookie expired, the edge 302s
            // into the Access flow and comes back here with a fresh
            // cookie. (The previous `/cdn-cgi/access/login` shortcut
            // isn't an Access endpoint on the app origin — Cloudflare
            // answers it with an empty page, which looked like a
            // blank-screen dead end.) Our service worker registers no
            // navigation fallback, so this genuinely hits the edge;
            // the `reauth` buster defeats the browser's HTTP cache
            // too. We stay on the current path, so Access returns the
            // user exactly where they were; the stray param is
            // ignored by every page.
            const url = new URL(window.location.href);
            url.searchParams.set('reauth', String(Date.now()));
            window.location.href = url.pathname + url.search;
          }}
        >
          Sign in again
        </button>
      </p>
    );
  }
  if (error instanceof NetworkError) {
    return <p className="muted">No connection. Will retry when you&apos;re back online.</p>;
  }
  return <p className="muted">Could not load. Trying again…</p>;
}
